import { useEffect, useRef, useState } from "react";
import { useRoadmap } from "@/context/RoadmapContext";
import { Filter } from "lucide-react";

const FILTERS = [
  { id: "all", label: "Tout" },
  { id: "in-progress", label: "En cours" },
  { id: "done", label: "Termin\u00e9es" },
];

export function CompletionBar({ tabName }) {
  const { taskStatuses } = useRoadmap();
  const barRef = useRef(null);
  const [stats, setStats] = useState({ total: 0, done: 0, inProgress: 0 });
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    const container = barRef.current?.parentElement;
    if (!container) return;

    const raf = requestAnimationFrame(() => {
      const els = container.querySelectorAll("[data-task-id]");
      let total = 0, done = 0, inProgress = 0;
      els.forEach((el) => {
        const id = el.getAttribute("data-task-id");
        if (id) {
          total++;
          const s = taskStatuses[id];
          if (s === "done") done++;
          else if (s === "in-progress") inProgress++;
        }
      });
      setStats({ total, done, inProgress });
    });

    return () => cancelAnimationFrame(raf);
  }, [taskStatuses]);

  // Apply filter via data attribute on parent container
  useEffect(() => {
    const container = barRef.current?.parentElement;
    if (!container) return;
    if (activeFilter === "all") {
      container.removeAttribute("data-task-filter");
    } else {
      container.setAttribute("data-task-filter", activeFilter);
    }
    return () => container.removeAttribute("data-task-filter");
  }, [activeFilter]);

  if (stats.total === 0) return <div ref={barRef} />;

  const pctDone = Math.round((stats.done / stats.total) * 100);
  const pctProg = Math.round((stats.inProgress / stats.total) * 100);

  return (
    <div ref={barRef} className="mb-6 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl p-4 sm:p-5 shadow-sm" data-testid="completion-bar">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-4">
          <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Progression
          </span>
          <span className="text-sm font-black text-gray-900 dark:text-white">
            {stats.done}/{stats.total}
            <span className="text-gray-400 dark:text-gray-500 font-normal ml-1">({pctDone}&nbsp;%)</span>
          </span>
        </div>
        {/* Filter buttons */}
        <div className="flex items-center gap-1.5" data-testid="task-filter-group">
          <Filter className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 mr-1" />
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(activeFilter === f.id ? "all" : f.id)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                activeFilter === f.id
                  ? f.id === "done"
                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 ring-1 ring-green-300 dark:ring-green-700"
                    : f.id === "in-progress"
                    ? "bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.24)] text-[#4c7e55] dark:text-[#2e6d3b] ring-1 ring-[#bfd1c3] dark:ring-[#2e6d3b]"
                    : "bg-[#eef3ee] dark:bg-[#243025] text-[#526756] dark:text-[#dfe6e0] ring-1 ring-[#c9d8cb] dark:ring-[#35513a]"
                  : "text-[#6b7d6e] dark:text-[#8ea391] hover:bg-[#f4f7f4] dark:hover:bg-[#202821]"
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
              {f.id === "done" && <span className="ml-1 opacity-70">{stats.done}</span>}
              {f.id === "in-progress" && <span className="ml-1 opacity-70">{stats.inProgress}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-2.5 bg-[#eef3ee] dark:bg-[#202821] rounded-full overflow-hidden flex">
        {pctDone > 0 && (
          <div
            className="h-full bg-green-500 transition-all duration-500 ease-out rounded-l-full"
            style={{ width: `${pctDone}%` }}
          />
        )}
        {pctProg > 0 && (
          <div
            className="h-full bg-[#4c7e55] dark:bg-[#2e6d3b] transition-all duration-500 ease-out"
            style={{ width: `${pctProg}%` }}
          />
        )}
      </div>
      <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Termin&eacute;es ({stats.done})
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[#4c7e55] dark:bg-[#2e6d3b] inline-block" /> En cours ({stats.inProgress})
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[#c9d8cb] dark:bg-[#35513a] inline-block" /> Restantes ({stats.total - stats.done - stats.inProgress})
        </span>
      </div>
    </div>
  );
}
